import * as React from "react";
import { useMemo } from "react";
import { ApplicationEntity } from "../../../../../application/domain/application/application-entity";
import { PaymentStatusTag } from "../../../../organisms/smart-apply/payment/payment-status";
import { useModal } from "../../../../../utils/custom-hooks";
import { BaseModal } from "../../../../atoms/base-modal";
import { PaymentDetail } from "../../../../organisms/smart-apply/payment/payment-detail/payment-detail";
import { AdjustmentModal } from "../../../../organisms/smart-apply/payment/adjustment-modal";
import { matchType } from "../../../../../application/domain/base/base-type";
import { formatAmount, Payment } from "@graffer-inc/cloud-gov-core";
import { ConfirmModal } from "../../../../organisms/common/confirm-modal";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { RBACButton } from "../../../../molecules/rbac-button";
import { useState } from "react";
import { RefundRequest } from "../../../../../application/domain/payment/refund-request";
import { RefundModalV2 } from "../../../../organisms/smart-apply/payment/refund-modal-v2";
import { Link } from "react-router-dom";
import { SmartApplyR } from "../../route";
import { PaymentDetailByPaymentJSON } from "../../../../../application/domain/payment/payment-summary";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";
import { MessageDialog } from "@graffer-inc/console-design-kit";

const PaymentListItem = ({
  payment: v,
  paymentDetail,
  applicationId,
  isClosed,
  withdrawn,
  procedure,
  index,
  refund,
  adjustExceedingOrDeficiency,
}: {
  applicationId: string;
  index: number;
  payment: Payment;
  paymentDetail?: PaymentDetailByPaymentJSON;
  isClosed: boolean;
  procedure: ProcedureEntity;
  withdrawn: (idx: number) => Promise<boolean>;
  refund: (req: RefundRequest, message?: string) => Promise<boolean>;
  adjustExceedingOrDeficiency: () => Promise<boolean>;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const latestPayeasyDetail = useMemo(() => {
    const payeasyDetail = paymentDetail?.payeasyDetail;
    return payeasyDetail && payeasyDetail.length > 0
      ? payeasyDetail[payeasyDetail.length - 1]
      : null;
  }, [paymentDetail]);
  const paymentDetailModal = useModal();
  const withdrawnConfirmModal = useModal();
  const refundModal = useModal();
  const adjustmentModel = useModal();
  const isRefundDisabled = !paymentDetail?.isRefundablePaymentMethod;
  const isCancelDisabled =
    latestPayeasyDetail !== null && !latestPayeasyDetail.isExpiredWithNoPayment;

  return (
    <>
      <div className="box">
        <nav className="level">
          <div className="level-left">
            <div className="level-item has-text-left">
              <div>
                <p className="has-text-weight-bold is-size-5">
                  {formatAmount(v.amount)} 円
                </p>
                <>
                  {v.status === "partial_refunded" && (
                    <p className="is-size-7">
                      (返金額 {formatAmount(v.amountRefunded)} 円)
                    </p>
                  )}
                  {(v.status === "deficient_payment" ||
                    v.status === "exceeding_payment") && (
                    <p className="is-size-7">
                      (総支払額 {formatAmount(v.amountCharged)} 円)
                    </p>
                  )}
                </>
              </div>
            </div>
            <div className="level-item has-text-left">
              <div className="has-text-weight-bold">
                <PaymentStatusTag status={v.status} />
              </div>
            </div>
            <p className="level-item has-text-centered">
              <Link
                to={SmartApplyR.ApplicationPaymentDetail.link({
                  id: applicationId,
                  index: index,
                })}
              >
                詳細
              </Link>
            </p>
          </div>
          <div className="level-right">
            {!isClosed &&
              matchType(v.status, {
                requested: (
                  <p className="level-item has-text-centered">
                    <RBACButton
                      target={procedure}
                      requirePermissions="applications.post_payment_withdrawn"
                      onClick={withdrawnConfirmModal.open}
                      buttonTypes={"secondary"}
                      buttonText={"キャンセルする"}
                    />
                  </p>
                ),
                paid: (
                  <p className="level-item has-text-centered">
                    <RBACButton
                      target={procedure}
                      requirePermissions="applications.payment_refund"
                      onClick={refundModal.open}
                      buttonTypes={"secondary"}
                      buttonText={"返金する"}
                    />
                  </p>
                ),
                partial_refunded: (
                  <p className="level-item has-text-centered">
                    <RBACButton
                      target={procedure}
                      requirePermissions="applications.payment_refund"
                      onClick={refundModal.open}
                      buttonTypes={"secondary"}
                      buttonText={"返金する"}
                    />
                  </p>
                ),
                refunded: null,
                cancelled: null,
                deficient_payment: (
                  <p className="level-item has-text-centered">
                    <RBACButton
                      target={procedure}
                      requirePermissions="applications.payment_adjustment"
                      onClick={adjustmentModel.open}
                      buttonTypes="secondary"
                      buttonText="入金不足を解消する"
                    />
                  </p>
                ),
                exceeding_payment: (
                  <p className="level-item has-text-centered">
                    <RBACButton
                      target={procedure}
                      requirePermissions="applications.payment_adjustment"
                      onClick={adjustmentModel.open}
                      buttonTypes="secondary"
                      buttonText="過入金を解消する"
                    />
                  </p>
                ),
              })}
          </div>
        </nav>
      </div>
      <BaseModal
        title="決済内容の詳細"
        isActive={paymentDetailModal.isActive}
        onClose={paymentDetailModal.close}
      >
        <section className="modal-card-body">
          <PaymentDetail payment={v} />
        </section>
        <footer className="modal-card-foot">
          <button
            className="button is-failure is-fullwidth"
            onClick={paymentDetailModal.close}
          >
            閉じる
          </button>
        </footer>
      </BaseModal>
      {withdrawnConfirmModal.isActive &&
        matchType(isCancelDisabled ? "disabled" : "enabled", {
          disabled: (
            <MessageDialog
              title="キャンセルができません"
              contents={
                <p>
                  申請者がペイジー支払いを発行しているため、キャンセルできません。
                  <br />
                  支払期限を過ぎた後にキャンセルするか、申請者に連絡をしてください。
                </p>
              }
              cancelText="閉じる"
              onCancel={withdrawnConfirmModal.close}
            />
          ),
          enabled: (
            <ConfirmModal
              title={"決済依頼のキャンセルの確認"}
              body={`${formatAmount(
                v.amount,
              )}円の決済依頼をキャンセルします。よろしいですか？`}
              isSuccessLoading={isSubmitting}
              successButtonText={"キャンセルする"}
              onSuccess={async () => {
                setIsSubmitting(true);
                if (await withdrawn(index)) {
                  withdrawnConfirmModal.close();
                }
                setIsSubmitting(false);
              }}
              isActive={withdrawnConfirmModal.isActive}
              onClose={withdrawnConfirmModal.close}
            />
          ),
        })}
      {refundModal.isActive &&
        paymentDetail &&
        matchType(isRefundDisabled ? "disabled" : "enabled", {
          disabled: (
            <MessageDialog
              title="返金ができません"
              contents={
                <p>
                  ペイジー支払いはシステム上で返金することができません。
                  申請者と連絡をとり、システム外での返金対応をしてください。
                </p>
              }
              cancelText="閉じる"
              onCancel={refundModal.close}
            />
          ),
          enabled: (
            <RefundModalV2
              summary={paymentDetail}
              isSubmitting={isSubmitting}
              onClose={refundModal.close}
              onOk={async (v, reason) => {
                setIsSubmitting(true);
                if (await refund(v, reason)) {
                  refundModal.close();
                }
                setIsSubmitting(false);
              }}
            />
          ),
        })}
      {adjustmentModel.isActive && paymentDetail && (
        <AdjustmentModal
          targetPayment={v}
          isSubmitting={false}
          onClose={adjustmentModel.close}
          onResolve={async () => {
            if (await adjustExceedingOrDeficiency()) {
              adjustmentModel.close();
            }
          }}
        />
      )}
    </>
  );
};

export const PaymentListV2: React.FC<{
  application: ApplicationEntity;
  procedure: ProcedureEntity;
  paymentDetailByPayments: GrafferGqlResult<PaymentDetailByPaymentJSON[]>;
  withdrawn: (paymentIndex: number) => Promise<boolean>;
  refund: (
    index: number,
    req: RefundRequest,
    message?: string,
  ) => Promise<boolean>;
  adjustExceedingOrDeficiency: (index: number) => Promise<boolean>;
}> = ({
  application,
  procedure,
  withdrawn,
  paymentDetailByPayments,
  refund,
  adjustExceedingOrDeficiency,
}) => {
  const length = application.getPaymentsV2().length;
  return (
    <>
      {application.getPaymentsV2().length === 0 && (
        <p className="notification">この申請に支払いはありません。</p>
      )}
      {application.getPaymentsV2().map((v, idx) => {
        return (
          <PaymentListItem
            key={`${length}-${idx}`}
            applicationId={application.id}
            index={idx}
            payment={new Payment(v)}
            paymentDetail={paymentDetailByPayments.data?.find(
              (s) => s.paymentId === v.paymentId,
            )}
            procedure={procedure}
            withdrawn={withdrawn}
            refund={(req, reason) => refund(idx, req, reason)}
            adjustExceedingOrDeficiency={() => adjustExceedingOrDeficiency(idx)}
            isClosed={application.isDone()}
          />
        );
      })}
    </>
  );
};
